import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Comentarios } from './Comentarios';
import { Metricas } from './Metricas';
import { Reacciones } from './Reacciones';
import { Adjuntos, ChipDeTipo, ContenidoRico, Galeria, REACCIONES } from './piezas';
import {
  type Adjunto, type Publicacion as Datos,
  useBorrarAdjunto, useConfirmarLectura, useDenunciarComentario, useDenunciarPublicacion,
  useReaccionar, useRetirarPublicacion,
} from './api';

/**
 * Publicacion — la tarjeta del muro.
 *
 * **Es la pieza más compartida de esta pantalla y no vive solo aquí**: la monta
 * también Comunidades, a través de `window.PortalReact.montarPublicaciones`.
 * Hasta el 2026-09-08 esa reutilización iba por `PageFeed.renderPostHTML`, y
 * migrar Novedades sin resolverlo habría dejado dos tarjetas —una React y una
 * vanilla— con reacciones, comentarios, denuncias, adjuntos, galería y métricas
 * duplicados, divergiendo a la primera corrección. La misma razón por la que
 * Inicio no reimplementó el muro el 2026-09-07.
 *
 * ## Todo el texto de personas es texto, no marcado
 *
 * El nombre del autor, el cargo, el área, la audiencia y el nombre real tras una
 * firma institucional son nodos de texto de React. En la versión vanilla eran
 * seis interpolaciones crudas en una plantilla de cadena, y `author_name` es el
 * peor de los seis: al firmar a nombre del área NO es el nombre del directorio,
 * sino `nombre_publico` —120 caracteres de texto libre que escribe quien
 * publica (`content.py:398`)— y va al muro que ve la organización entera.
 *
 * El único marcado que se inserta es el cuerpo, por `ContenidoRico`, que pasa
 * por `Utils.renderPostContent`.
 *
 * ## La lápida
 *
 * Una publicación retirada conserva su tarjeta y sus comentarios (decisión del
 * 2026-08-04): quien comentó no ve desaparecer su propio comentario. Lo que
 * pierde es la barra de acciones, la caja de comentar y —desde el
 * 2026-08-26— el aviso de confirmar lectura, que pedía confirmar la lectura de
 * un cuadro vacío.
 */
export function Publicacion({
  publicacion, alCambiar,
}: {
  publicacion: Datos;
  /** Se llama cuando algo de la publicación cambió y la lista de fuera debería
   *  refrescarse. En Novedades lo hace react-query solo; en el muro incrustado
   *  de Comunidades lo recarga el vanilla. */
  alCambiar?: () => void;
}) {
  const p = publicacion;
  const yo = Auth.getUser();
  const esMio = yo?.id != null && yo.id === p.author_id;
  const esStaff = Auth.isStaff();

  const [hiloDesplegado, setHiloDesplegado] = useState(false);
  // Una publicación retirada enseña su hilo SIEMPRE, y esto es una derivación y
  // no un estado inicial a propósito.
  //
  // Con `useState(p.eliminada)` el valor se calcula una sola vez, al montar. La
  // tarjeta que alguien acaba de retirar ya está montada —react-query solo le
  // cambia los datos—, así que el hilo no se reabría: quien retiraba su
  // publicación veía desaparecer los comentarios que otras personas le habían
  // dejado, que es exactamente lo que la decisión del 2026-08-04 quería evitar.
  // Volvían al recargar, así que desde fuera parecía que se habían borrado.
  // Encontrado en el navegador el 2026-09-08, retirando una publicación de
  // prueba con un comentario.
  const comentariosAbiertos = hiloDesplegado || p.eliminada === true;
  const [verMetricas, setVerMetricas] = useState(false);
  const [retirando, setRetirando] = useState(false);
  const [denunciando, setDenunciando] = useState(false);
  const [adjuntoABorrar, setAdjuntoABorrar] = useState<Adjunto | null>(null);

  const confirmarLectura = useConfirmarLectura();
  const borrarAdjunto = useBorrarAdjunto();

  const esSistema = p.author_role === 'sistema';
  const esGestion = p.author_role === 'admin' || p.author_role === 'coordinador';
  const nombre = p.author_name || 'Usuario';

  // Retirar lo suyo, o moderar lo ajeno. Se pregunta por el PERMISO y no por el
  // nombre del rol: comparar `role !== 'colaborador'` esconde el botón a los
  // roles personalizados que sí están autorizados.
  const puedeRetirar = !p.eliminada && (esMio || Auth.can('feed', 'manage'));
  const puedeDenunciar = !p.eliminada && !esMio;
  const puedeVerMetricas = esStaff || esMio;
  const puedeBorrarAdjuntos = esStaff || esMio;

  return (
    <article
      className="overflow-hidden rounded-md border border-line bg-surface"
      data-post-id={p.id}
      data-type={p.post_type}
    >
      {/* ── Cabecera ────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start gap-2 px-4 pt-4">
        <Avatar
          nombre={nombre}
          foto={p.author_avatar}
          tamano={44}
          insignia={
            esSistema ? <i className="bi bi-building-fill-check text-4" aria-hidden="true" /> : undefined
          }
        />

        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span className="text-3 font-bold text-ink">{nombre}</span>

            {/* La insignia OFICIAL: navy sobre el naranja de marca, 7,04. Es el
                único sitio del muro donde el acento pinta una superficie, y se
                lo gana: dice «esto lo firma la institución». */}
            {esSistema && (
              <span className="rounded-xs bg-accent px-1.5 py-0.5 text-1 font-extrabold text-brand">
                OFICIAL
              </span>
            )}
            {!esSistema && esGestion && p.author_role && (
              <Badge tone="neutral">{p.author_role}</Badge>
            )}
            {/* «Jefe» iba con tinte naranja y tinta `--orange-dark`: 2,50 de
                contraste en claro y 3,62 en oscuro, por debajo del mínimo. El
                tono `warn` del sistema es el mismo naranja con la tinta medida
                (5,07 / 5,82). Es el defecto de las insignias de estado de
                Solicitudes, otra vez. */}
            {p.author_is_manager && (
              <Badge tone="warn">
                <i className="bi bi-person-badge" aria-hidden="true" /> Jefe
              </Badge>
            )}
          </p>

          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2 text-muted">
            {p.author_position && (
              <span className="inline-flex items-center gap-1">
                <i className="bi bi-briefcase" aria-hidden="true" />
                {p.author_position}
              </span>
            )}
            {p.author_area && (
              <span className="inline-flex items-center gap-1">
                <i className="bi bi-diagram-3" aria-hidden="true" />
                {p.author_area}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <i className="bi bi-clock" aria-hidden="true" />
              {Utils.relativeTime(p.created_at)}
            </span>
            {/* Solo viaja para staff. Es la trazabilidad de una firma
                institucional: quién la escribió de verdad. */}
            {p.author_real && (
              <span
                className="inline-flex items-center gap-1"
                title="Solo visible para la administración"
              >
                <i className="bi bi-eye" aria-hidden="true" />
                Publicado por {p.author_real}
              </span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {p.audience_label && (
            <Badge tone="info" title="Audiencia del comunicado">
              <i className="bi bi-bullseye" aria-hidden="true" />
              {p.audience_label}
            </Badge>
          )}
          <ChipDeTipo tipo={p.post_type} />
          {puedeVerMetricas && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Métricas de alcance"
              title="Métricas de alcance"
              onClick={() => setVerMetricas(true)}
            >
              <i className="bi bi-bar-chart" aria-hidden="true" />
            </Button>
          )}
          {puedeRetirar && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Eliminar publicación"
              title="Eliminar publicación"
              onClick={() => setRetirando(true)}
            >
              <i className="bi bi-trash3" aria-hidden="true" />
            </Button>
          )}
          {puedeDenunciar && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Reportar publicación"
              title="Reportar publicación"
              onClick={() => setDenunciando(true)}
            >
              <i className="bi bi-flag" aria-hidden="true" />
            </Button>
          )}
        </div>
      </header>

      {/* ── Cuerpo ──────────────────────────────────────────────────── */}
      <div className="px-4 pt-3">
        {p.eliminada ? (
          <p
            className="flex items-center gap-2 rounded-sm border border-dashed border-line-strong
                       bg-sunken px-3 py-2.5 text-3 text-ink-2"
          >
            <i className="bi bi-trash3 shrink-0" aria-hidden="true" />
            {p.eliminada_por_su_autor
              ? 'Esta publicación fue eliminada por su autor.'
              : 'Esta publicación fue retirada.'}
          </p>
        ) : (
          <ContenidoRico html={p.content} />
        )}
      </div>

      {/* ── Confirmación de lectura ─────────────────────────────────── */}
      {p.requires_ack && !p.eliminada && (
        <div className="px-4 pt-3">
          {p.has_read ? (
            <p className="text-2 font-semibold text-ok-ink">
              <i className="bi bi-check-circle-fill" aria-hidden="true" /> Lectura confirmada
            </p>
          ) : (
            <div
              className="flex flex-wrap items-center gap-3 rounded-sm border border-warn/40
                         bg-warn/12 px-3 py-2.5"
            >
              <p className="min-w-40 flex-1 text-2 text-ink">
                <i className="bi bi-megaphone-fill mr-1 text-brand-ink" aria-hidden="true" />
                Comunicado importante — confirma que lo leíste.
              </p>
              <Button
                size="sm"
                loading={confirmarLectura.isPending}
                onClick={() =>
                  confirmarLectura.mutate(p.id, {
                    onSuccess: () => {
                      Utils.toast('Lectura confirmada', 'success');
                      alCambiar?.();
                    },
                    onError: (e: unknown) =>
                      Utils.toast((e as Error).message || 'No se pudo confirmar', 'error'),
                  })
                }
              >
                <i className="bi bi-check2" aria-hidden="true" /> Confirmar lectura
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Medios ──────────────────────────────────────────────────── */}
      {p.gallery?.length ? (
        <Galeria fotos={p.gallery} />
      ) : p.image_url ? (
        <button
          type="button"
          className="mt-3 block w-full focus-visible:focus-anillo"
          onClick={() => Utils.openLightbox([{ url: p.image_url as string }], 0)}
          aria-label="Ampliar la imagen de la publicación"
        >
          <img src={p.image_url} loading="lazy" alt="" className="w-full object-cover" />
        </button>
      ) : null}

      <Adjuntos
        adjuntos={p.attachments ?? []}
        puedeBorrar={puedeBorrarAdjuntos}
        onBorrar={setAdjuntoABorrar}
      />

      {/* ── Encuesta ────────────────────────────────────────────────── */}
      {p.post_type === 'encuesta' && p.survey_id && (
        <div className="px-4 pt-3">
          <Button block onClick={() => APP.navigate(`encuestas/${p.survey_id}`)}>
            <i className="bi bi-clipboard-check" aria-hidden="true" /> Responder encuesta
          </Button>
        </div>
      )}

      {/* ── Reacciones y comentarios ────────────────────────────────── */}
      {!p.eliminada && (
        <BarraDeAcciones
          publicacion={p}
          comentariosAbiertos={comentariosAbiertos}
          onAlternarComentarios={() => setHiloDesplegado((v) => !v)}
        />
      )}

      {comentariosAbiertos && (
        <Comentarios postId={p.id} cerrada={p.eliminada === true} />
      )}

      {/* ── Diálogos ────────────────────────────────────────────────── */}
      {verMetricas && (
        <Metricas postId={p.id} abierto onCerrar={() => setVerMetricas(false)} />
      )}
      {retirando && (
        <DialogoRetirar
          postId={p.id}
          onCerrar={() => setRetirando(false)}
          alCambiar={alCambiar}
        />
      )}
      {denunciando && (
        <DialogoDenunciar postId={p.id} onCerrar={() => setDenunciando(false)} />
      )}
      {adjuntoABorrar && (
        <Dialog
          abierto
          onCambio={() => setAdjuntoABorrar(null)}
          titulo="Eliminar adjunto"
          descripcion={`«${adjuntoABorrar.filename}» dejará de estar en la publicación. No se puede deshacer.`}
          ancho="max-w-md"
          pie={
            <>
              <Button variant="ghost" onClick={() => setAdjuntoABorrar(null)} autoFocus>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                loading={borrarAdjunto.isPending}
                onClick={() =>
                  borrarAdjunto.mutate(
                    { postId: p.id, adjuntoId: adjuntoABorrar.id },
                    {
                      onSuccess: () => {
                        Utils.toast('Adjunto eliminado', 'success');
                        setAdjuntoABorrar(null);
                        alCambiar?.();
                      },
                      onError: (e: unknown) =>
                        Utils.toast((e as Error).message || 'No se pudo eliminar', 'error'),
                    },
                  )
                }
              >
                Eliminar
              </Button>
            </>
          }
        />
      )}
    </article>
  );
}

/* ── Barra de reacciones y comentar ────────────────────────────────────── */

/**
 * El resumen de reacciones y los cuatro botones.
 *
 * El total vive en la barra de resumen, que es donde se enseña. Antes se
 * guardaba en el botón «Me gusta», que además lo pintaba: una publicación con
 * una sola celebración anunciaba «Me gusta 1».
 *
 * El recuento se lleva en estado local y no se invalida la lista al reaccionar:
 * volver a pedir veinte publicaciones para cambiar un número haría saltar el
 * muro entero bajo el dedo de quien está leyendo.
 */
function BarraDeAcciones({
  publicacion, comentariosAbiertos, onAlternarComentarios,
}: {
  publicacion: Datos;
  comentariosAbiertos: boolean;
  onAlternarComentarios: () => void;
}) {
  const p = publicacion;
  const [mia, setMia] = useState<string | null>(p.user_reaction ?? null);
  const [total, setTotal] = useState(p.reaction_count ?? 0);
  const [verQuien, setVerQuien] = useState(false);
  const reaccionar = useReaccionar();

  const pulsar = (clave: string) => {
    const eraMia = mia === clave;
    const teniaAlguna = mia !== null;
    reaccionar.mutate(
      { postId: p.id, reaccion: clave },
      {
        onSuccess: () => {
          setMia(eraMia ? null : clave);
          if (eraMia) setTotal((n) => Math.max(0, n - 1));
          else if (!teniaAlguna) setTotal((n) => n + 1);
        },
        // Antes era un `catch {}`: si la publicación acababa de retirarse, el
        // 404 no producía NINGUNA señal. El botón no hacía nada y la persona
        // volvía a pulsar.
        onError: (e: unknown) =>
          Utils.toast((e as Error).message || 'No se pudo registrar tu reacción', 'error'),
      },
    );
  };

  return (
    <>
      {total > 0 && (
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-line px-4 py-2">
          <button
            type="button"
            onClick={() => setVerQuien(true)}
            // El suelo táctil también aquí. Medido a 375 px con puntero grueso
            // emulado: era el ÚNICO control de la pantalla por debajo de 44 px
            // —19 de alto—, porque es texto suelto y no hereda de `Button`. La
            // trampa que anotó Solicitudes al cerrarse: el bloque
            // `@media (pointer: coarse)` de `design-system.css` solo nombra
            // clases del vanilla, así que un control React con clases propias
            // no cae ahí y hay que darle los 44 px a mano.
            className="inline-flex items-center gap-2 rounded-xs text-2 text-ink-2
                       transition-colors hover:text-brand-ink focus-visible:focus-anillo
                       pointer-coarse:min-h-11"
          >
            <span className="flex" aria-hidden="true">
              {REACCIONES.map((r, i) => (
                <i
                  key={r.clave}
                  className={cn(
                    'bi grid size-4.5 place-items-center rounded-full border-2 border-surface',
                    'bg-brand text-1 text-on-brand',
                    r.activo,
                    i > 0 && '-ml-1.5',
                  )}
                />
              ))}
            </span>
            {total} {total === 1 ? 'reacción' : 'reacciones'}
          </button>
          <span className="text-1 text-muted">
            <i className="bi bi-shield-check" aria-hidden="true" /> auditable
          </span>
        </div>
      )}

      <div
        className={cn(
          'flex flex-wrap gap-1 px-2 py-1.5',
          total > 0 ? 'border-t border-line' : 'mt-3 border-t border-line',
        )}
      >
        {REACCIONES.map((r) => {
          const activa = mia === r.clave;
          return (
            <button
              key={r.clave}
              type="button"
              aria-pressed={activa}
              // El nombre va SIEMPRE en el atributo y no solo en el texto: por
              // debajo de 640 px la etiqueta se oculta y el botón se queda con
              // el icono, que no dice «Me gusta» a quien no lo ve.
              aria-label={r.etiqueta}
              onClick={() => pulsar(r.clave)}
              className={cn(
                'inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-2',
                'text-2 font-semibold transition-colors focus-visible:focus-anillo',
                'pointer-coarse:min-h-11',
                activa ? 'bg-brand/8 text-brand-ink' : 'text-ink-2 hover:bg-sunken',
              )}
            >
              <i className={cn('bi', activa ? r.activo : r.apagado)} aria-hidden="true" />
              <span className="max-sm:hidden">{r.etiqueta}</span>
            </button>
          );
        })}
        <button
          type="button"
          aria-expanded={comentariosAbiertos}
          aria-label="Comentar"
          onClick={onAlternarComentarios}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-2
                     text-2 font-semibold text-ink-2 transition-colors hover:bg-sunken
                     focus-visible:focus-anillo pointer-coarse:min-h-11"
        >
          <i className="bi bi-chat-dots" aria-hidden="true" />
          <span className="max-sm:hidden">Comentar</span>
          {p.comment_count ? <span className="text-muted">{p.comment_count}</span> : null}
        </button>
      </div>

      {verQuien && <Reacciones postId={p.id} onCerrar={() => setVerQuien(false)} />}
    </>
  );
}

/* ── Retirar ───────────────────────────────────────────────────────────── */

/**
 * Retirar la publicación propia (o moderar la ajena).
 *
 * Diálogo y no panel: es irreversible y no hace falta seguir viendo el muro
 * detrás. Decisión de producto del 2026-08-04 — el autor RETIRA lo suyo, no lo
 * edita, y lo retirado deja una marca en vez de un hueco.
 */
function DialogoRetirar({
  postId, onCerrar, alCambiar,
}: {
  postId: number;
  onCerrar: () => void;
  alCambiar?: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const retirar = useRetirarPublicacion();

  return (
    <Dialog
      abierto
      onCambio={onCerrar}
      titulo="Eliminar publicación"
      descripcion="Se retirará del muro. En su lugar quedará una nota que dice que la eliminaste, y los comentarios seguirán visibles. Queda registrada en la auditoría con su contenido original y no se puede deshacer."
      ancho="max-w-md"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar} autoFocus>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            loading={retirar.isPending}
            onClick={() =>
              retirar.mutate(
                { postId, motivo },
                {
                  onSuccess: (r) => {
                    Utils.toast(r?.message || 'Publicación eliminada', 'success', 4000);
                    onCerrar();
                    alCambiar?.();
                  },
                  onError: (e: unknown) =>
                    Utils.toast((e as Error).message || 'No se pudo eliminar', 'error', 5000),
                },
              )
            }
          >
            <i className="bi bi-trash3" aria-hidden="true" /> Eliminar
          </Button>
        </>
      }
    >
      <label className="block">
        <span className="mb-1 block text-2 font-semibold text-ink-2">Motivo (opcional)</span>
        <input
          type="text"
          maxLength={500}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Por ejemplo: me equivoqué de área"
          className="w-full rounded-sm border border-field-border bg-surface px-3 py-2 text-3
                     text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
        />
      </label>
    </Dialog>
  );
}

/* ── Denunciar ─────────────────────────────────────────────────────────── */

const MOTIVOS: string[] = [
  'Lenguaje ofensivo',
  'Acoso o discriminación',
  'Contenido inapropiado',
  'Spam o publicidad',
  'Otro',
];

/**
 * Reportar una publicación.
 *
 * «Otro» abre un campo de texto: sin él, el motivo llegaba al administrador
 * literalmente como la palabra «Otro» y sin contexto ninguno.
 */
export function DialogoDenunciar({
  postId, comentarioId, onCerrar,
}: {
  postId?: number;
  comentarioId?: number;
  onCerrar: () => void;
}) {
  const [motivo, setMotivo] = useState<string>('Lenguaje ofensivo');
  const [detalle, setDetalle] = useState('');
  const denunciarPost = useDenunciarPublicacion();
  const denunciarComentario = useDenunciarComentario();
  const esComentario = comentarioId != null;
  const enviando = denunciarPost.isPending || denunciarComentario.isPending;

  const texto = motivo !== 'Otro' ? motivo : detalle.trim() ? `Otro: ${detalle.trim()}` : 'Otro';

  const enviar = () => {
    const alTerminar = {
      onSuccess: (r: { message?: string }) => {
        Utils.toast(r?.message || 'Gracias por reportar', 'success', 4000);
        onCerrar();
      },
      onError: (e: unknown) =>
        Utils.toast((e as Error).message || 'No se pudo enviar el reporte', 'error'),
    };
    if (esComentario) {
      denunciarComentario.mutate({ comentarioId: comentarioId as number, motivo: texto }, alTerminar);
    } else {
      denunciarPost.mutate({ postId: postId as number, motivo: texto }, alTerminar);
    }
  };

  return (
    <Dialog
      abierto
      onCambio={onCerrar}
      titulo={esComentario ? 'Reportar comentario' : 'Reportar publicación'}
      descripcion={
        esComentario
          ? 'Tu reporte es confidencial y lo revisará el equipo de moderación.'
          : 'Ayúdanos a mantener un espacio sano. Un administrador lo revisará.'
      }
      ancho="max-w-md"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar} autoFocus>
            Cancelar
          </Button>
          <Button variant="destructive" loading={enviando} onClick={enviar}>
            <i className="bi bi-flag-fill" aria-hidden="true" /> Reportar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="block">
          <span className="mb-1 block text-2 font-semibold text-ink-2">Motivo</span>
          <select
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full rounded-sm border border-field-border bg-surface px-3 py-2 text-3
                       text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
          >
            {MOTIVOS.map((m) => (
              <option key={m} value={m}>
                {m === 'Lenguaje ofensivo' ? 'Lenguaje ofensivo o grosero' : m}
              </option>
            ))}
          </select>
        </label>

        {motivo === 'Otro' && (
          <label className="block">
            <span className="mb-1 block text-2 font-semibold text-ink-2">Cuéntanos qué pasa</span>
            <input
              type="text"
              maxLength={280}
              autoFocus
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Describe brevemente el motivo"
              className="w-full rounded-sm border border-field-border bg-surface px-3 py-2 text-3
                         text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
            />
          </label>
        )}
      </div>
    </Dialog>
  );
}
